import {
	isBelarusRegistrationOksm,
	isDomesticRegistrationOksm,
	isKyrgyzstanRegistrationOksm,
	isRussiaRegistrationOksm,
} from './countries';
import {RegistrationStep} from './steps';

export type DocumentsApiField =
	| 'passportPhoto'
	| 'passportRegistrationPhoto'
	| 'passportSelfie'
	| 'residenceDocument'
	| 'residentEntryDocument'
	| 'residentDepartureDocument';

export type DocumentsSlotConfig = {
	id: string;
	label: string;
	apiField: DocumentsApiField;
};

export type DocumentPhotoScreenConfig = {
	title: string;
	slots: DocumentsSlotConfig[];
	/**
	 * Минимум заполненных слотов для валидности.
	 * Не задано — нужны все слоты. Для «Фото паспорта» (множественное) — хотя бы одно.
	 */
	minFilledSlots?: number;
	/** Общий лейбл для множественного поля (вместо лейбла каждого слота). */
	groupLabel?: string;
};

export type ResidenceDocumentScreenConfig = {
	title: string;
	subtitle: string;
	requireAddress: boolean;
};

export type DocumentsFlowConfig = {
	passport: DocumentPhotoScreenConfig;
	residence: ResidenceDocumentScreenConfig | null;
};

export type DocumentsFlowInput = {
	citizenshipOksmCode: string | null;
	residenceOksmCode: string | null;
	residenceCountryName?: string | null;
	identificationDocumentType?: string | null;
};

const KZ_OKSM = '398';

const PASSPORT_SCREEN_TITLE = 'Введите паспортные данные';
const DOCUMENTS_SCREEN_TITLE = 'Загрузите фото документов';

const SLOT = {
	identitySpread: {
		id: 'identitySpread',
		label: 'Разворот с ФИО',
		apiField: 'passportPhoto' as const,
	},
	identitySpreadWithPhoto: {
		id: 'identitySpread',
		label: 'Разворот с ФИО и фото',
		apiField: 'passportPhoto' as const,
	},
	registrationSpread: {
		id: 'registrationSpread',
		label: 'Разворот с регистрацией',
		apiField: 'passportRegistrationPhoto' as const,
	},
	/** Селфи для всех гражданств: `identificationDocument.photos.passportSelfie`. */
	passportSelfie: {
		id: 'passportSelfie',
		label: 'Селфи с паспортом',
		apiField: 'passportSelfie' as const,
	},
	arrivalFront: {
		id: 'arrivalFront',
		label: 'Лицевая сторона документа о прибытии в РФ',
		apiField: 'residentEntryDocument' as const,
	},
	arrivalBack: {
		id: 'arrivalBack',
		label: 'Обратная сторона документа о прибытии в РФ',
		apiField: 'residentDepartureDocument' as const,
	},
	idFront: {
		id: 'idFront',
		label: 'Лицевая сторона удостоверения',
		apiField: 'passportPhoto' as const,
	},
	idBack: {
		id: 'idBack',
		label: 'Обратная сторона удостоверения',
		apiField: 'passportRegistrationPhoto' as const,
	},
	residenceDocument: {
		id: 'residenceDocument',
		label: 'Документ подтверждающий место проживания',
		apiField: 'residenceDocument' as const,
	},
};

/**
 * РФ: множественное «Фото паспорта» — ровно 3 фото обязательны.
 * Адрес регистрации — отдельное текстовое поле на том же экране.
 */
const RU_PASSPORT_PHOTOS: DocumentPhotoScreenConfig = {
	title: PASSPORT_SCREEN_TITLE,
	groupLabel: 'Фото паспорта',
	minFilledSlots: 3,
	slots: [SLOT.identitySpread, SLOT.registrationSpread, SLOT.passportSelfie],
};

/** Беларусь / Киргизия: разворот и селфи. Документ о прибытии добавляется отдельно. */
const BY_OR_KG_PASSPORT_PHOTOS: DocumentPhotoScreenConfig = {
	title: PASSPORT_SCREEN_TITLE,
	groupLabel: 'Фото паспорта',
	minFilledSlots: 2,
	slots: [
		{
			id: 'identitySpread',
			label: 'Разворот паспорта с ФИО',
			apiField: 'passportPhoto',
		},
		SLOT.passportSelfie,
	],
};

/** Армения, Казахстан (паспорт), Украина, «Другое» + проживание в РФ/РБ. */
const FOREIGN_DOMESTIC_RESIDENCE_SLOTS: DocumentPhotoScreenConfig = {
	title: DOCUMENTS_SCREEN_TITLE,
	groupLabel: 'Фото паспорта',
	slots: [SLOT.identitySpreadWithPhoto, SLOT.passportSelfie],
};

/** Те же гражданства, проживание не в РФ/РБ: разворот и селфи. Документ о проживании — отдельный экран после налогового идентификатора. */
const IDENTITY_AND_SELFIE_SLOTS: DocumentPhotoScreenConfig = {
	title: DOCUMENTS_SCREEN_TITLE,
	groupLabel: 'Фото паспорта',
	slots: [SLOT.identitySpread, SLOT.passportSelfie],
};

const KZ_ID_SLOTS: DocumentPhotoScreenConfig = {
	title: DOCUMENTS_SCREEN_TITLE,
	groupLabel: 'Фото удостоверения',
	slots: [SLOT.idFront, SLOT.idBack, SLOT.passportSelfie],
};

const isKazakhstan = (oksmCode: string | null): boolean => {
	return String(oksmCode ?? '').trim() === KZ_OKSM;
};

const isKazakhIdCard = (documentType: string | null | undefined): boolean => {
	return documentType === 'kazakh_id_card';
};

const RESIDENCE_DOCUMENT_TITLE = 'Подтвердите место проживания';
const RESIDENCE_DOCUMENT_UPLOAD_LABEL = 'Загрузите документ, подтверждающий место проживания';

export const getResidenceDocumentSubtitle = (_residenceCountryName?: string | null): string => {
	return RESIDENCE_DOCUMENT_UPLOAD_LABEL;
};

const createResidenceConfig = (): ResidenceDocumentScreenConfig => ({
	title: RESIDENCE_DOCUMENT_TITLE,
	subtitle: RESIDENCE_DOCUMENT_UPLOAD_LABEL,
	requireAddress: true,
});

/**
 * Фото на том же экране, что и поля документа.
 * Шаги как у РФ: отдельного экрана «Загрузите фото» нет, отличаются только поля.
 */
export const combinesDocumentPhotosWithDetails = (
	_citizenshipOksmCode?: string | null,
): boolean => {
	return true;
};

/** Обе стороны документа о прибытии в РФ — гражданство не РФ и резидентство РФ. */
const withRfArrivalDocumentPhotos = (
	config: DocumentPhotoScreenConfig,
	input: DocumentsFlowInput,
): DocumentPhotoScreenConfig => {
	if (isRussiaRegistrationOksm(input.citizenshipOksmCode) || !isRussiaRegistrationOksm(input.residenceOksmCode)) {
		return config;
	}

	return {
		...config,
		slots: [...config.slots, SLOT.arrivalFront, SLOT.arrivalBack],
		...(config.minFilledSlots == null ? {} : {minFilledSlots: config.minFilledSlots + 2}),
	};
};

/** Подтверждение проживания — отдельный экран после налогового идентификатора, если страна не РФ. */
const resolveResidenceScreen = (residenceOksmCode: string | null): ResidenceDocumentScreenConfig | null => {
	if (!residenceOksmCode || isRussiaRegistrationOksm(residenceOksmCode)) {
		return null;
	}

	return createResidenceConfig();
};

export const resolveDocumentsFlow = (input: DocumentsFlowInput): DocumentsFlowConfig => {
	const citizenshipDomestic = isDomesticRegistrationOksm(input.citizenshipOksmCode);
	const residenceDomestic = isDomesticRegistrationOksm(input.residenceOksmCode);
	const kyrgyzstan = isKyrgyzstanRegistrationOksm(input.citizenshipOksmCode);
	const kazakhIdCard = isKazakhstan(input.citizenshipOksmCode) && isKazakhIdCard(input.identificationDocumentType);
	const residence = resolveResidenceScreen(input.residenceOksmCode);

	if (citizenshipDomestic) {
		return {
			passport: isBelarusRegistrationOksm(input.citizenshipOksmCode)
				? withRfArrivalDocumentPhotos(BY_OR_KG_PASSPORT_PHOTOS, input)
				: RU_PASSPORT_PHOTOS,
			residence,
		};
	}

	/** Киргизия: фото как у Беларуси. Документ о прибытии — только при резидентстве РФ. */
	if (kyrgyzstan) {
		return {
			passport: withRfArrivalDocumentPhotos(BY_OR_KG_PASSPORT_PHOTOS, input),
			residence,
		};
	}

	if (kazakhIdCard) {
		return {
			passport: withRfArrivalDocumentPhotos(KZ_ID_SLOTS, input),
			residence,
		};
	}

	return {
		passport: withRfArrivalDocumentPhotos(
			residenceDomestic ? FOREIGN_DOMESTIC_RESIDENCE_SLOTS : IDENTITY_AND_SELFIE_SLOTS,
			input,
		),
		residence,
	};
};

export const getAllDocumentsFlowSlots = (flow: DocumentsFlowConfig): DocumentsSlotConfig[] => {
	const slots = [...flow.passport.slots];

	if (flow.residence) {
		slots.push(SLOT.residenceDocument);
	}

	return slots;
};

export const getNextDocumentsStep = (step: RegistrationStep): RegistrationStep => {
	if (step === RegistrationStep.DocumentPhotos) {
		return RegistrationStep.DocumentDetails;
	}

	if (step === RegistrationStep.DocumentDetails) {
		return RegistrationStep.IdentificationInn;
	}

	if (step === RegistrationStep.ResidenceDocument) {
		return RegistrationStep.IdentityReview;
	}

	return RegistrationStep.IdentificationInn;
};

/** Ключ флоу документов: гражданство, резидентство и тип документа Казахстана. */
export const getRegistrationDocumentsFlowKey = (
	citizenshipOksmCode: string | null,
	residenceOksmCode: string | null,
	foreignDocumentType: string | null,
): string => {
	const kazakhDocumentType = isKazakhstan(citizenshipOksmCode) ? foreignDocumentType : null;

	return [citizenshipOksmCode, residenceOksmCode, kazakhDocumentType].join(':');
};

export const getPreviousDocumentsStep = (
	step: RegistrationStep,
	_flow: DocumentsFlowConfig,
	/** Для иностранцев фото — отдельный шаг; для РФ фото на экране паспортных данных. */
	includeDocumentPhotosStep = true,
): RegistrationStep => {
	if (step === RegistrationStep.ResidenceDocument) {
		return RegistrationStep.IdentificationInn;
	}

	if (step === RegistrationStep.DocumentDetails) {
		return includeDocumentPhotosStep
			? RegistrationStep.DocumentPhotos
			: RegistrationStep.PersonalInfo;
	}

	return RegistrationStep.PersonalInfo;
};
